import type { HostingType, StreamProvider as StreamingProviderType, StreamProviderType, StreamStatus, TechnicalStreamStatus } from '@/types/streaming';

export interface RadioStation {
  id: number;
  name: string;
  slug: string;
  frequency: string;
  city: string;
  state: string;
  country: string;
  category: string;
  language: string;
  hostName: string;
  listeners: number;
  rating: number;
  isLive: boolean;
  logoUrl: string;
  description: string;
  radioScore: number;
  cabinaMessages: number;
  recentPosts: number;
  isPremium: boolean;
  isTopRadio: boolean;
  cabinaActiva: boolean;
  // ── Modelo híbrido de hosting ──
  hostingType: HostingType;
  streamProvider: StreamingProviderType;
  providerType: StreamProviderType;
  streamUrl: string;
  streamStatus: StreamStatus;
  // ── Estado técnico de streaming (CentovaCast) ──
  technicalStreamStatus: TechnicalStreamStatus;
  streamAvailable: boolean;
  listenerCount: number;
  currentMetadata: string | null;
  lastTechnicalSyncAt: string | null;
  publicStreamUrl: string | null;
  publicSecureStreamUrl: string | null;
  isPlatformRadio?: boolean;
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export const radioStations: RadioStation[] = [
  // ── RFW RADIO OFICIAL — Siempre #1, no eliminable, no modificable ──
  {
    id: 0,
    name: "RFW Radio",
    slug: "rfw-radio",
    frequency: "87.00 RFW",
    city: "Global",
    state: "Plataforma RFW",
    country: "Global",
    category: "Oficial",
    language: "Español",
    hostName: "RFW Oficial",
    listeners: 15820,
    rating: 5.0,
    isLive: true,
    logoUrl: "https://readdy.ai/api/search-image?query=RFW%20Radio%20official%20logo%20design%2C%20circular%20modern%20emblem%20with%20radio%20waves%20and%20star%2C%20minimalist%20professional%20broadcast%20identity%2C%20warm%20amber%20and%20coral%20gradient%20colors%2C%20clean%20geometric%20shapes%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20style&width=200&height=200&seq=rfw-radio-logo&orientation=squarish",
    description: "La estación oficial de RadioFrecuenciaWeb. Contenido exclusivo, entrevistas, programas originales y lo mejor de la plataforma. Siempre disponible, siempre en vivo.",
    radioScore: 10.0,
    cabinaMessages: 1240,
    recentPosts: 85,
    isPremium: true,
    isTopRadio: true,
    cabinaActiva: true,
    hostingType: 'rfw_hosting',
    providerType: 'rfw_hosted',
    streamProvider: 'icecast',
    streamUrl: 'https://stream.rfwradio.com:8000/rfw-live',
    streamStatus: 'online',
    technicalStreamStatus: 'active',
    streamAvailable: true,
    listenerCount: 15820,
    currentMetadata: 'RFW Radio Oficial - Transmitiendo 24/7',
    lastTechnicalSyncAt: '2026-07-14T12:00:00Z',
    publicStreamUrl: 'https://stream.rfwradio.com:8000/rfw-live',
    publicSecureStreamUrl: 'https://stream.rfwradio.com:443/rfw-live'
  },

  // ── SECUENCIALES NORMALES ──
  {
    id: 2,
    name: "Radio Fe y Esperanza",
    slug: slugify("Radio Fe y Esperanza"),
    frequency: "87.03 RFW",
    city: "Miami",
    state: "Florida",
    country: "Estados Unidos",
    category: "Cristiana",
    language: "Español",
    hostName: "Pastora María Elena",
    listeners: 5632,
    rating: 4.9,
    isLive: true,
    logoUrl: "https://readdy.ai/api/search-image?query=Christian%20radio%20station%20logo%20with%20dove%20symbol%2C%20circular%20minimalist%20design%2C%20soft%20warm%20colors%20white%20and%20gentle%20golden%20tones%2C%20clean%20modern%20broadcast%20emblem%2C%20spiritual%20community%20radio%2C%20isolated%20on%20transparent%20background%2C%20vector%20style%20flat%20design&width=200&height=200&seq=logo-2&orientation=squarish",
    description: "Transmitiendo esperanza y fe para toda la comunidad cristiana de habla hispana.",
    radioScore: 8.7,
    cabinaMessages: 234,
    recentPosts: 18,
    isPremium: false,
    isTopRadio: true,
    cabinaActiva: true,
    hostingType: 'external',
    providerType: 'direct_server',
    streamProvider: 'shoutcast',
    streamUrl: 'https://stream.feyesperanza.com:9000/live',
    streamStatus: 'online',
    technicalStreamStatus: 'active',
    streamAvailable: true,
    listenerCount: 5632,
    currentMetadata: 'Pastora María Elena - Reflexiones de Fe',
    lastTechnicalSyncAt: '2026-07-14T11:30:00Z',
    publicStreamUrl: 'https://stream.feyesperanza.com:9000/live',
    publicSecureStreamUrl: null
  },
  {
    id: 5,
    name: "Rock Total Radio",
    slug: slugify("Rock Total Radio"),
    frequency: "87.01 RFW",
    city: "Los Ángeles",
    state: "California",
    country: "Estados Unidos",
    category: "Rock",
    language: "Español",
    hostName: "Alex 'Metal' Torres",
    listeners: 7234,
    rating: 4.6,
    isLive: true,
    logoUrl: "https://readdy.ai/api/search-image?query=Rock%20radio%20station%20logo%20with%20electric%20guitar%20and%20lightning%20bolt%2C%20circular%20design%20black%20and%20red%20color%20scheme%2C%20modern%20rock%20broadcast%20emblem%2C%20energetic%20and%20bold%20style%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-5&orientation=squarish",
    description: "Rock clásico y contemporáneo. Las mejores guitarras del mundo en un solo lugar.",
    radioScore: 8.1,
    cabinaMessages: 312,
    recentPosts: 25,
    isPremium: false,
    isTopRadio: true,
    cabinaActiva: true,
    hostingType: 'external',
    providerType: 'direct_server',
    streamProvider: 'icecast',
    streamUrl: 'https://stream.rocktotal.com:8000/rock',
    streamStatus: 'online',
    technicalStreamStatus: 'active',
    streamAvailable: true,
    listenerCount: 7234,
    currentMetadata: 'Rock Total Radio - Alex Torres al aire',
    lastTechnicalSyncAt: '2026-07-14T11:45:00Z',
    publicStreamUrl: 'https://stream.rocktotal.com:8000/rock',
    publicSecureStreamUrl: null
  },
  {
    id: 6,
    name: "Radio Comunitaria Unida",
    slug: slugify("Radio Comunitaria Unida"),
    frequency: "87.02 RFW",
    city: "Chicago",
    state: "Illinois",
    country: "Estados Unidos",
    category: "Comunitaria",
    language: "Español",
    hostName: "Colectivo Vecinal",
    listeners: 1567,
    rating: 4.3,
    isLive: true,
    logoUrl: "https://readdy.ai/api/search-image?query=Community%20radio%20station%20logo%20with%20people%20holding%20hands%20in%20circle%2C%20minimalist%20design%20with%20warm%20earth%20tones%2C%20green%20and%20orange%20colors%2C%20inclusive%20broadcast%20emblem%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-6&orientation=squarish",
    description: "La voz de nuestra comunidad. Cultura, eventos locales y participación ciudadana.",
    radioScore: 5.8,
    cabinaMessages: 67,
    recentPosts: 8,
    isPremium: false,
    isTopRadio: false,
    cabinaActiva: true,
    hostingType: 'external',
    providerType: 'custom_url',
    streamProvider: 'external',
    streamUrl: 'https://comunitariaunida.radio123.com:8000/stream',
    streamStatus: 'offline',
    technicalStreamStatus: 'offline',
    streamAvailable: false,
    listenerCount: 0,
    currentMetadata: null,
    lastTechnicalSyncAt: '2026-07-14T10:00:00Z',
    publicStreamUrl: 'https://comunitariaunida.radio123.com:8000/stream',
    publicSecureStreamUrl: null
  },

  // ── PREMIUM (frecuencias reservadas / vendidas) ──
  {
    id: 1,
    name: "Radio Nueva Actitud",
    slug: slugify("Radio Nueva Actitud"),
    frequency: "94.70 RFW",
    city: "Santa Rosa",
    state: "California",
    country: "Estados Unidos",
    category: "Pop",
    language: "Español",
    hostName: "Laura Méndez",
    listeners: 2847,
    rating: 4.7,
    isLive: true,
    logoUrl: "https://readdy.ai/api/search-image?query=Modern%20minimalist%20radio%20station%20logo%20design%20with%20musical%20notes%20and%20frequency%20waves%2C%20clean%20circular%20emblem%20style%2C%20warm%20golden%20and%20white%20color%20palette%2C%20simple%20geometric%20shapes%2C%20professional%20broadcast%20aesthetic%2C%20isolated%20on%20transparent%20background%2C%20high%20contrast%20vector%20style&width=200&height=200&seq=logo-1&orientation=squarish",
    description: "La mejor música pop y entretenimiento en vivo desde el corazón de California.",
    radioScore: 7.9,
    cabinaMessages: 156,
    recentPosts: 14,
    isPremium: true,
    isTopRadio: true,
    cabinaActiva: true,
    hostingType: 'rfw_hosting',
    providerType: 'rfw_hosted',
    streamProvider: 'azuracast',
    streamUrl: 'https://azuracast.rfwradio.com/nueva-actitud',
    streamStatus: 'online',
    technicalStreamStatus: 'active',
    streamAvailable: true,
    listenerCount: 2847,
    currentMetadata: 'Laura Méndez - Nueva Actitud en Vivo',
    lastTechnicalSyncAt: '2026-07-14T12:05:00Z',
    publicStreamUrl: 'https://azuracast.rfwradio.com/nueva-actitud',
    publicSecureStreamUrl: 'https://azuracast.rfwradio.com/nueva-actitud'
  },
  {
    id: 9,
    name: "La Kalle 99.9",
    slug: slugify("La Kalle 99.9"),
    frequency: "99.90 RFW",
    city: "Phoenix",
    state: "Arizona",
    country: "Estados Unidos",
    category: "Pop",
    language: "Español",
    hostName: "DJ Paula & Bryan",
    listeners: 6340,
    rating: 4.4,
    isLive: true,
    logoUrl: "https://readdy.ai/api/search-image?query=Pop%20music%20radio%20station%20logo%20with%20headphones%20and%20music%20equalizer%20bars%2C%20circular%20design%20vibrant%20pink%20and%20coral%20colors%2C%20modern%20youthful%20broadcast%20emblem%2C%20energetic%20style%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-9&orientation=squarish",
    description: "Los éxitos del momento y la música que está marcando tendencia.",
    radioScore: 8.4,
    cabinaMessages: 289,
    recentPosts: 22,
    isPremium: true,
    isTopRadio: true,
    cabinaActiva: true,
    hostingType: 'external',
    providerType: 'direct_server',
    streamProvider: 'shoutcast',
    streamUrl: 'https://stream.lakalle.com:9000/pop',
    streamStatus: 'online',
    technicalStreamStatus: 'active',
    streamAvailable: true,
    listenerCount: 6340,
    currentMetadata: 'DJ Paula & Bryan - Éxitos del Momento',
    lastTechnicalSyncAt: '2026-07-14T11:55:00Z',
    publicStreamUrl: 'https://stream.lakalle.com:9000/pop',
    publicSecureStreamUrl: null
  },
  {
    id: 3,
    name: "La Mexicana FM",
    slug: slugify("La Mexicana FM"),
    frequency: "103.50 RFW",
    city: "Houston",
    state: "Texas",
    country: "Estados Unidos",
    category: "Regional Mexicana",
    language: "Español",
    hostName: "Don Fernando Ramírez",
    listeners: 12450,
    rating: 4.8,
    isLive: true,
    logoUrl: "https://readdy.ai/api/search-image?query=Mexican%20regional%20radio%20station%20logo%20with%20guitar%20and%20sombrero%20elements%2C%20circular%20design%20warm%20orange%20and%20green%20colors%2C%20clean%20modern%20broadcast%20style%2C%20festive%20vibrant%20feel%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-3&orientation=squarish",
    description: "El sonido auténtico de México directo a tus oídos. Banda, norteño y más.",
    radioScore: 9.1,
    cabinaMessages: 567,
    recentPosts: 35,
    isPremium: true,
    isTopRadio: true,
    cabinaActiva: true,
    hostingType: 'external',
    providerType: 'custom_url',
    streamProvider: 'external',
    streamUrl: 'https://stream.lamexicana.com:8000/regional',
    streamStatus: 'online',
    technicalStreamStatus: 'active',
    streamAvailable: true,
    listenerCount: 12450,
    currentMetadata: 'Don Fernando Ramírez - La Mexicana en Vivo',
    lastTechnicalSyncAt: '2026-07-14T12:10:00Z',
    publicStreamUrl: 'https://stream.lamexicana.com:8000/regional',
    publicSecureStreamUrl: null
  },
  {
    id: 4,
    name: "Noticias 24/7 Radio",
    slug: slugify("Noticias 24/7 Radio"),
    frequency: "107.90 RFW",
    city: "Nueva York",
    state: "Nueva York",
    country: "Estados Unidos",
    category: "Noticias",
    language: "Español",
    hostName: "Elena Castellanos",
    listeners: 8921,
    rating: 4.5,
    isLive: false,
    logoUrl: "https://readdy.ai/api/search-image?query=News%20radio%20station%20logo%20design%20with%20microphone%20and%20globe%20icon%2C%20circular%20minimalist%20emblem%2C%20blue%20and%20white%20professional%20color%20scheme%2C%20broadcast%20journalism%20style%2C%20isolated%20on%20transparent%20background%2C%20clean%20vector%20design&width=200&height=200&seq=logo-4&orientation=squarish",
    description: "Información actualizada las 24 horas del día. Noticias locales e internacionales.",
    radioScore: 8.0,
    cabinaMessages: 445,
    recentPosts: 52,
    isPremium: true,
    isTopRadio: false,
    cabinaActiva: false,
    hostingType: 'external',
    providerType: 'direct_server',
    streamProvider: 'icecast',
    streamUrl: 'https://stream.noticias247.com:8000/news',
    streamStatus: 'offline',
    technicalStreamStatus: 'suspended',
    streamAvailable: false,
    listenerCount: 0,
    currentMetadata: null,
    lastTechnicalSyncAt: '2026-07-14T08:00:00Z',
    publicStreamUrl: 'https://stream.noticias247.com:8000/news',
    publicSecureStreamUrl: null
  },

  // ── EXPANSIÓN ──
  {
    id: 7,
    name: "Deportes Total Radio",
    slug: slugify("Deportes Total Radio"),
    frequency: "120.00 RFW",
    city: "Dallas",
    state: "Texas",
    country: "Estados Unidos",
    category: "Deportes",
    language: "Español",
    hostName: "Jorge 'El Comentarista' Ruiz",
    listeners: 10890,
    rating: 4.8,
    isLive: true,
    logoUrl: "https://readdy.ai/api/search-image?query=Sports%20radio%20station%20logo%20with%20soccer%20ball%20and%20trophy%20icon%2C%20circular%20design%20dark%20blue%20and%20gold%20color%20scheme%2C%20dynamic%20broadcast%20emblem%2C%20energetic%20sports%20feel%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-7&orientation=squarish",
    description: "La cobertura deportiva más completa. Fútbol, béisbol, baloncesto y más.",
    radioScore: 9.3,
    cabinaMessages: 623,
    recentPosts: 41,
    isPremium: false,
    isTopRadio: true,
    cabinaActiva: true,
    hostingType: 'external',
    providerType: 'custom_url',
    streamProvider: 'external',
    streamUrl: 'https://stream.deportestotal.com:8000/sports',
    streamStatus: 'online',
    technicalStreamStatus: 'active',
    streamAvailable: true,
    listenerCount: 10890,
    currentMetadata: 'Jorge Ruiz - Cobertura Deportiva Total',
    lastTechnicalSyncAt: '2026-07-14T12:15:00Z',
    publicStreamUrl: 'https://stream.deportestotal.com:8000/sports',
    publicSecureStreamUrl: null
  },
  {
    id: 8,
    name: "Radio Internacional Voz",
    slug: slugify("Radio Internacional Voz"),
    frequency: "140.00 RFW",
    city: "San Francisco",
    state: "California",
    country: "Estados Unidos",
    category: "Internacional",
    language: "Multilingüe",
    hostName: "Carlos Mondragón",
    listeners: 4523,
    rating: 4.2,
    isLive: false,
    logoUrl: "https://readdy.ai/api/search-image?query=International%20radio%20station%20logo%20with%20globe%20and%20sound%20waves%2C%20circular%20minimalist%20design%2C%20blue%20and%20teal%20color%20palette%2C%20global%20broadcast%20emblem%2C%20multicultural%20feel%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-8&orientation=squarish",
    description: "Conectando culturas a través de las ondas. Música y noticias de todo el mundo.",
    radioScore: 6.8,
    cabinaMessages: 98,
    recentPosts: 12,
    isPremium: false,
    isTopRadio: false,
    cabinaActiva: false,
    hostingType: 'external',
    providerType: 'direct_server',
    streamProvider: 'shoutcast',
    streamUrl: 'https://stream.internacionalvoz.com:9000/world',
    streamStatus: 'offline',
    technicalStreamStatus: 'offline',
    streamAvailable: false,
    listenerCount: 0,
    currentMetadata: null,
    lastTechnicalSyncAt: '2026-07-14T09:30:00Z',
    publicStreamUrl: 'https://stream.internacionalvoz.com:9000/world',
    publicSecureStreamUrl: null
  },
  {
    id: 10,
    name: "Radio Voz del Pueblo",
    slug: slugify("Radio Voz del Pueblo"),
    frequency: "160.00 RFW",
    city: "Denver",
    state: "Colorado",
    country: "Estados Unidos",
    category: "Comunitaria",
    language: "Español",
    hostName: "Comunidad Activa",
    listeners: 2890,
    rating: 4.1,
    isLive: true,
    logoUrl: "https://readdy.ai/api/search-image?query=Local%20community%20radio%20station%20logo%20with%20city%20skyline%20and%20microphone%2C%20circular%20design%20warm%20brown%20and%20cream%20colors%2C%20grassroots%20broadcast%20emblem%2C%20local%20charm%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-10&orientation=squarish",
    description: "La plataforma para que tu voz sea escuchada. Programación hecha por y para la gente.",
    radioScore: 5.4,
    cabinaMessages: 134,
    recentPosts: 9,
    isPremium: false,
    isTopRadio: false,
    cabinaActiva: true,
    hostingType: 'external',
    providerType: 'custom_url',
    streamProvider: 'external',
    streamUrl: 'https://stream.vozdelpueblo.com:8000/community',
    streamStatus: 'online',
    technicalStreamStatus: 'active',
    streamAvailable: true,
    listenerCount: 2890,
    currentMetadata: 'Comunidad Activa - La Voz del Pueblo',
    lastTechnicalSyncAt: '2026-07-14T11:20:00Z',
    publicStreamUrl: 'https://stream.vozdelpueblo.com:8000/community',
    publicSecureStreamUrl: null
  },
  {
    id: 11,
    name: "Radio Vida Nueva",
    slug: slugify("Radio Vida Nueva"),
    frequency: "180.00 RFW",
    city: "Orlando",
    state: "Florida",
    country: "Estados Unidos",
    category: "Cristiana",
    language: "Español",
    hostName: "Hermano José Luis",
    listeners: 4156,
    rating: 4.7,
    isLive: true,
    logoUrl: "https://readdy.ai/api/search-image?query=Christian%20worship%20radio%20station%20logo%20with%20cross%20and%20sunrise%20symbols%2C%20circular%20design%20soft%20blue%20and%20white%20colors%2C%20peaceful%20spiritual%20broadcast%20emblem%2C%20minimalist%20style%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-11&orientation=squarish",
    description: "Música de adoración y mensajes inspiradores para toda la familia cristiana.",
    radioScore: 7.5,
    cabinaMessages: 178,
    recentPosts: 16,
    isPremium: false,
    isTopRadio: false,
    cabinaActiva: true,
    hostingType: 'rfw_hosting',
    providerType: 'rfw_hosted',
    streamProvider: 'icecast',
    streamUrl: 'https://stream.rfwradio.com/vida-nueva',
    streamStatus: 'online',
    technicalStreamStatus: 'active',
    streamAvailable: true,
    listenerCount: 4156,
    currentMetadata: 'Hermano José Luis - Mensajes de Esperanza',
    lastTechnicalSyncAt: '2026-07-14T12:00:00Z',
    publicStreamUrl: 'https://stream.rfwradio.com/vida-nueva',
    publicSecureStreamUrl: 'https://stream.rfwradio.com/vida-nueva'
  },
  {
    id: 12,
    name: "Banda Radio MX",
    slug: slugify("Banda Radio MX"),
    frequency: "200.00 RFW",
    city: "San Antonio",
    state: "Texas",
    country: "Estados Unidos",
    category: "Regional Mexicana",
    language: "Español",
    hostName: "El Compa Chuy",
    listeners: 9780,
    rating: 4.6,
    isLive: true,
    logoUrl: "https://readdy.ai/api/search-image?query=Mexican%20banda%20music%20radio%20station%20logo%20with%20trumpet%20and%20accordion%2C%20circular%20design%20red%20white%20and%20green%20colors%2C%20festive%20broadcast%20emblem%2C%20traditional%20Mexican%20vibe%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-12&orientation=squarish",
    description: "Pura banda, norteño y regional mexicano. La tradición musical de México.",
    radioScore: 8.8,
    cabinaMessages: 432,
    recentPosts: 29,
    isPremium: false,
    isTopRadio: true,
    cabinaActiva: true,
    hostingType: 'external',
    providerType: 'custom_url',
    streamProvider: 'external',
    streamUrl: 'https://stream.bandaradiomx.com:8000/banda',
    streamStatus: 'online',
    technicalStreamStatus: 'active',
    streamAvailable: true,
    listenerCount: 9780,
    currentMetadata: 'El Compa Chuy - Pura Banda en Vivo',
    lastTechnicalSyncAt: '2026-07-14T12:20:00Z',
    publicStreamUrl: 'https://stream.bandaradiomx.com:8000/banda',
    publicSecureStreamUrl: null
  },

  // ── EXPANSIÓN INTERNACIONAL ──
  {
    id: 13,
    name: "Jazz Nocturno Radio",
    slug: slugify("Jazz Nocturno Radio"),
    frequency: "91.30 RFW",
    city: "Nueva Orleans",
    state: "Luisiana",
    country: "Estados Unidos",
    category: "Jazz",
    language: "Inglés",
    hostName: "Robert 'Blue' Johnson",
    listeners: 3456,
    rating: 4.8,
    isLive: true,
    logoUrl: "https://readdy.ai/api/search-image?query=Jazz%20radio%20station%20logo%20with%20saxophone%20and%20crescent%20moon%2C%20circular%20design%20deep%20amber%20and%20warm%20gold%20colors%2C%20smooth%20sophisticated%20broadcast%20emblem%2C%20classic%20New%20Orleans%20jazz%20feel%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-13&orientation=squarish",
    description: "El mejor jazz clásico y contemporáneo. Desde el corazón de Nueva Orleans para el mundo.",
    radioScore: 8.2,
    cabinaMessages: 210,
    recentPosts: 15,
    isPremium: false,
    isTopRadio: false,
    cabinaActiva: true,
    hostingType: 'external',
    providerType: 'direct_server',
    streamProvider: 'icecast',
    streamUrl: 'https://stream.jazznocturno.com:8000/jazz',
    streamStatus: 'online',
    technicalStreamStatus: 'active',
    streamAvailable: true,
    listenerCount: 3456,
    currentMetadata: 'Robert Blue Johnson - Jazz Nocturno en Vivo',
    lastTechnicalSyncAt: '2026-07-14T12:25:00Z',
    publicStreamUrl: 'https://stream.jazznocturno.com:8000/jazz',
    publicSecureStreamUrl: null
  },
  {
    id: 14,
    name: "Electro Vibes FM",
    slug: slugify("Electro Vibes FM"),
    frequency: "96.50 RFW",
    city: "Miami",
    state: "Florida",
    country: "Estados Unidos",
    category: "Electrónica",
    language: "Inglés",
    hostName: "DJ Spark & MC Luna",
    listeners: 8120,
    rating: 4.5,
    isLive: true,
    logoUrl: "https://readdy.ai/api/search-image?query=Electronic%20music%20radio%20station%20logo%20with%20synthesizer%20waveform%20and%20neon%20accents%2C%20circular%20design%20vibrant%20cyan%20and%20magenta%20colors%2C%20futuristic%20broadcast%20emblem%2C%20energetic%20club%20music%20feel%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-14&orientation=squarish",
    description: "House, techno y electrónica 24/7. Los mejores DJs mezclando en vivo.",
    radioScore: 8.6,
    cabinaMessages: 398,
    recentPosts: 31,
    isPremium: false,
    isTopRadio: true,
    cabinaActiva: true,
    hostingType: 'external',
    providerType: 'direct_server',
    streamProvider: 'shoutcast',
    streamUrl: 'https://stream.electrovibes.com:9000/electro',
    streamStatus: 'online',
    technicalStreamStatus: 'active',
    streamAvailable: true,
    listenerCount: 8120,
    currentMetadata: 'DJ Spark & MC Luna - Electro Vibes 24/7',
    lastTechnicalSyncAt: '2026-07-14T12:30:00Z',
    publicStreamUrl: 'https://stream.electrovibes.com:9000/electro',
    publicSecureStreamUrl: null
  },
  {
    id: 15,
    name: "Radio Infantil Aventura",
    slug: slugify("Radio Infantil Aventura"),
    frequency: "97.80 RFW",
    city: "Orlando",
    state: "Florida",
    country: "Estados Unidos",
    category: "Infantil",
    language: "Español",
    hostName: "Tía Rosita",
    listeners: 1890,
    rating: 4.8,
    isLive: false,
    logoUrl: "https://readdy.ai/api/search-image?query=Childrens%20radio%20station%20logo%20with%20cartoon%20rocket%20and%20stars%2C%20circular%20design%20playful%20bright%20orange%20and%20yellow%20colors%2C%20fun%20educational%20broadcast%20emblem%2C%20kid%20friendly%20cheerful%20style%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-15&orientation=squarish",
    description: "Contenido educativo y divertido para los más pequeños. Cuentos, música y aventuras.",
    radioScore: 6.2,
    cabinaMessages: 45,
    recentPosts: 7,
    isPremium: false,
    isTopRadio: false,
    cabinaActiva: false,
    hostingType: 'external',
    providerType: 'custom_url',
    streamProvider: 'external',
    streamUrl: '',
    streamStatus: 'pending',
    technicalStreamStatus: 'not_configured',
    streamAvailable: false,
    listenerCount: 0,
    currentMetadata: null,
    lastTechnicalSyncAt: null,
    publicStreamUrl: null,
    publicSecureStreamUrl: null
  },
  {
    id: 16,
    name: "Radio Salsa Brava",
    slug: slugify("Radio Salsa Brava"),
    frequency: "110.70 RFW",
    city: "San Juan",
    state: "Puerto Rico",
    country: "Puerto Rico",
    category: "Salsa",
    language: "Español",
    hostName: "Carlos 'El Sonero' Vega",
    listeners: 10550,
    rating: 4.9,
    isLive: true,
    logoUrl: "https://readdy.ai/api/search-image?query=Salsa%20music%20radio%20station%20logo%20with%20congas%20and%20tropical%20palm%20silhouette%2C%20circular%20design%20vibrant%20orange%20and%20yellow%20colors%2C%20festive%20Caribbean%20broadcast%20emblem%2C%20energetic%20Latin%20rhythm%20feel%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-16&orientation=squarish",
    description: "La mejor salsa, timba y son cubano. Ritmo latino las 24 horas del día.",
    radioScore: 9.5,
    cabinaMessages: 512,
    recentPosts: 38,
    isPremium: false,
    isTopRadio: true,
    cabinaActiva: true,
    hostingType: 'external',
    providerType: 'custom_url',
    streamProvider: 'external',
    streamUrl: 'https://stream.salsabrava.com:8000/salsa',
    streamStatus: 'online',
    technicalStreamStatus: 'active',
    streamAvailable: true,
    listenerCount: 10550,
    currentMetadata: 'Carlos El Sonero Vega - Salsa Brava en Vivo',
    lastTechnicalSyncAt: '2026-07-14T12:35:00Z',
    publicStreamUrl: 'https://stream.salsabrava.com:8000/salsa',
    publicSecureStreamUrl: null
  },
  {
    id: 17,
    name: "Clásica Mundial Radio",
    slug: slugify("Clásica Mundial Radio"),
    frequency: "130.50 RFW",
    city: "Boston",
    state: "Massachusetts",
    country: "Estados Unidos",
    category: "Clásica",
    language: "Multilingüe",
    hostName: "Maestro Alberto Crespo",
    listeners: 2340,
    rating: 4.6,
    isLive: false,
    logoUrl: "https://readdy.ai/api/search-image?query=Classical%20music%20radio%20station%20logo%20with%20violin%20and%20conductor%20baton%2C%20circular%20design%20elegant%20cream%20and%20burgundy%20colors%2C%20refined%20broadcast%20emblem%2C%20sophisticated%20orchestral%20feel%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-17&orientation=squarish",
    description: "Las obras maestras de la música clásica. Beethoven, Mozart, Bach y más.",
    radioScore: 7.1,
    cabinaMessages: 87,
    recentPosts: 10,
    isPremium: false,
    isTopRadio: false,
    cabinaActiva: false,
    hostingType: 'external',
    providerType: 'direct_server',
    streamProvider: 'icecast',
    streamUrl: 'https://stream.clasicamundial.com:8000/classical',
    streamStatus: 'offline',
    technicalStreamStatus: 'error',
    streamAvailable: false,
    listenerCount: 0,
    currentMetadata: null,
    lastTechnicalSyncAt: '2026-07-14T10:15:00Z',
    publicStreamUrl: 'https://stream.clasicamundial.com:8000/classical',
    publicSecureStreamUrl: null
  },
  {
    id: 18,
    name: "Radio Reggae Vibes",
    slug: slugify("Radio Reggae Vibes"),
    frequency: "150.30 RFW",
    city: "Kingston",
    state: "Jamaica",
    country: "Jamaica",
    category: "Reggae",
    language: "Inglés",
    hostName: "Rasta Mike",
    listeners: 5670,
    rating: 4.7,
    isLive: true,
    logoUrl: "https://readdy.ai/api/search-image?query=Reggae%20radio%20station%20logo%20with%20lion%20of%20Judah%20and%20palm%20tree%2C%20circular%20design%20vibrant%20green%20yellow%20and%20red%20colors%2C%20authentic%20Jamaican%20broadcast%20emblem%2C%20chilled%20island%20music%20feel%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-18&orientation=squarish",
    description: "Reggae, dancehall y ritmos caribeños. Buenas vibras desde Jamaica.",
    radioScore: 8.3,
    cabinaMessages: 267,
    recentPosts: 19,
    isPremium: false,
    isTopRadio: false,
    cabinaActiva: true,
    hostingType: 'external',
    providerType: 'custom_url',
    streamProvider: 'external',
    streamUrl: 'https://stream.reggaevibes.com:8000/reggae',
    streamStatus: 'online',
    technicalStreamStatus: 'active',
    streamAvailable: true,
    listenerCount: 5670,
    currentMetadata: 'Rasta Mike - Reggae Vibes desde Jamaica',
    lastTechnicalSyncAt: '2026-07-14T12:40:00Z',
    publicStreamUrl: 'https://stream.reggaevibes.com:8000/reggae',
    publicSecureStreamUrl: null
  },
  {
    id: 19,
    name: "Radio Hip Hop Nation",
    slug: slugify("Radio Hip Hop Nation"),
    frequency: "170.40 RFW",
    city: "Atlanta",
    state: "Georgia",
    country: "Estados Unidos",
    category: "Hip Hop",
    language: "Inglés",
    hostName: "Big Mike & DJ Scratch",
    listeners: 14200,
    rating: 4.5,
    isLive: true,
    logoUrl: "https://readdy.ai/api/search-image?query=Hip%20hop%20radio%20station%20logo%20with%20turntable%20and%20graffiti%20style%20crown%2C%20circular%20design%20bold%20black%20and%20gold%20colors%2C%20urban%20broadcast%20emblem%2C%20street%20music%20culture%20feel%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-19&orientation=squarish",
    description: "Hip hop, rap y cultura urbana. Los beats más duros desde Atlanta.",
    radioScore: 9.2,
    cabinaMessages: 698,
    recentPosts: 44,
    isPremium: false,
    isTopRadio: true,
    cabinaActiva: true,
    hostingType: 'external',
    providerType: 'direct_server',
    streamProvider: 'shoutcast',
    streamUrl: 'https://stream.hiphopnation.com:9000/hiphop',
    streamStatus: 'online',
    technicalStreamStatus: 'active',
    streamAvailable: true,
    listenerCount: 14200,
    currentMetadata: 'Big Mike & DJ Scratch - Hip Hop Nation Live',
    lastTechnicalSyncAt: '2026-07-14T12:45:00Z',
    publicStreamUrl: 'https://stream.hiphopnation.com:9000/hiphop',
    publicSecureStreamUrl: null
  },
  {
    id: 20,
    name: "Radio Country Road",
    slug: slugify("Radio Country Road"),
    frequency: "190.20 RFW",
    city: "Nashville",
    state: "Tennessee",
    country: "Estados Unidos",
    category: "Country",
    language: "Inglés",
    hostName: "Billy Ray Thompson",
    listeners: 7230,
    rating: 4.4,
    isLive: true,
    logoUrl: "https://readdy.ai/api/search-image?query=Country%20music%20radio%20station%20logo%20with%20acoustic%20guitar%20and%20cowboy%20hat%2C%20circular%20design%20warm%20brown%20and%20cream%20colors%2C%20rustic%20broadcast%20emblem%2C%20authentic%20Nashville%20country%20feel%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-20&orientation=squarish",
    description: "Country clásico y moderno. Desde el corazón de Nashville, Tennessee.",
    radioScore: 7.8,
    cabinaMessages: 324,
    recentPosts: 23,
    isPremium: false,
    isTopRadio: false,
    cabinaActiva: true,
    hostingType: 'external',
    providerType: 'custom_url',
    streamProvider: 'external',
    streamUrl: 'https://stream.countryroad.com:8000/country',
    streamStatus: 'online',
    technicalStreamStatus: 'active',
    streamAvailable: true,
    listenerCount: 7230,
    currentMetadata: 'Billy Ray Thompson - Country Road Nashville',
    lastTechnicalSyncAt: '2026-07-14T12:50:00Z',
    publicStreamUrl: 'https://stream.countryroad.com:8000/country',
    publicSecureStreamUrl: null
  }
];

export const categories: string[] = [
  "Oficial",
  "Cristiana",
  "Regional Mexicana",
  "Noticias",
  "Deportes",
  "Pop",
  "Rock",
  "Comunitaria",
  "Internacional",
  "Jazz",
  "Electrónica",
  "Infantil",
  "Salsa",
  "Clásica",
  "Reggae",
  "Hip Hop",
  "Country"
];

export const categoriesWithIcons: { name: string; icon: string }[] = [
  { name: "Oficial", icon: "ri-verified-badge-line" },
  { name: "Regional Mexicana", icon: "ri-music-2-line" },
  { name: "Noticias", icon: "ri-newspaper-line" },
  { name: "Deportes", icon: "ri-football-line" },
  { name: "Pop", icon: "ri-music-line" },
  { name: "Rock", icon: "ri-flashlight-line" },
  { name: "Comunitaria", icon: "ri-group-line" },
  { name: "Internacional", icon: "ri-global-line" },
  { name: "Jazz", icon: "ri-music-2-line" },
  { name: "Electrónica", icon: "ri-headphone-line" },
  { name: "Infantil", icon: "ri-emotion-happy-line" },
  { name: "Salsa", icon: "ri-music-line" },
  { name: "Clásica", icon: "ri-music-2-line" },
  { name: "Reggae", icon: "ri-music-line" },
  { name: "Hip Hop", icon: "ri-headphone-line" },
  { name: "Country", icon: "ri-music-2-line" }
];